import { getConfig } from '@services/app.config.loader';
import { loadSentryIO } from '@app/core/sentry.service';
import { getBrand } from '@helpers/brand.utils';
import { Brand } from '@constants/brands';

const splitScripts = (scriptsStr: string): string[] => {
  return scriptsStr.replace(' ', '').split(',');
};

/**
 * loads all scripts into the site based on config
 */
export const loadGlobalScripts = (): void => {
  loadSentryIO();
  loadSalesforceTracking();
  loadHeadScripts();
  loadBodyScripts();
};

const loadSalesforceTracking = () => {
  const titleNodes = document.getElementsByTagName('title');
  const config = { attributes: true, childList: true, subtree: true };

  if (getBrand() === Brand.HBW210) {
    defineReadonlyGlobalProperty('piAId', '1107041');
    defineReadonlyGlobalProperty('piCId', '265365');
    defineReadonlyGlobalProperty('piHostname', 'go.2-10.com');
  } else {
    defineReadonlyGlobalProperty('piAId', '1008092');
    defineReadonlyGlobalProperty('piCId', '265365');
    defineReadonlyGlobalProperty('piHostname', 'trk.pd.ahs.com');
  }

  const observer = new MutationObserver((mutationList, obv) => {
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.src =
      ('https:' == document.location.protocol ? 'https://' : 'http://') + window.piHostname + '/pd.js';
    const c = document.getElementsByTagName('script')[0];
    c.parentNode.insertBefore(s, c);
    obv.disconnect();
  });
  observer.observe(titleNodes[0], config);
}

const loadHeadScripts = (): void => {
  try {
    const scripts = getConfig('headScripts');
    if (scripts) {
      for (const key of splitScripts(scripts)) {
        try {
          const src = getConfig(key);
          if (src) {
            appendScript(src, true);
          }
        } catch (e) {
          console.error(`failed to load ${key}`, e);
        }
      }
    }
  } catch (e) {
    console.error('failed to load scripts', e);
  }
};

const loadBodyScripts = (): void => {
  try {
    const scripts = getConfig('bodyScripts');
    if (scripts) {
      for (const key of splitScripts(scripts)) {
        try {
          const src = getConfig(key);
          if (src) {
            appendScript(src, false);
          }
        } catch (e) {
          console.error(`failed to load ${key}`, e);
        }
      }
    }
  } catch (e) {
    console.error('failed to load scripts', e);
  }
};

/**
 * Appends a single script src to either the end of head or body
 * @param toHead true if script to be appending to end of head. Otherwise, end of body
 */
export const appendScript = (scriptSrc: string, toHead: boolean): void => {
  const script = document.createElement('script');
  script.src = scriptSrc;
  script.async = true;
  if (toHead) {
    document.head.appendChild(script);
  } else {
    document.body.appendChild(script);
  }
};

/**
 * Removes script from dom with matching src name
 */
export const removeScript = (scriptSrc: string): void => {
  const allsuspects = document.getElementsByTagName('script');
  for (let i = allsuspects.length; i >= 0; i--) {
    if (
      allsuspects[i] &&
      allsuspects[i].getAttribute('src') &&
      allsuspects[i].getAttribute('src').indexOf(`${scriptSrc}`) !== -1
    ) {
      // Remove script
      allsuspects[i].parentNode.removeChild(allsuspects[i]);
    }
  }
};

const defineReadonlyGlobalProperty = (name: string, value: unknown): void => {
  // If the property already exists on the window object, do not override it or redefine it.
  if (Object.getOwnPropertyDescriptor(window, name)) return;

  Object.defineProperty(window, name, {
    get() {
      return value;
    },
    set(_) {
      console.trace(`Attempt to override ${name} was blocked.`);
    },
    configurable: false,
    enumerable: true,
  });
};